/**
 * citationUtils — shared canonicalisation for citation sources.
 *
 * Athena emits one source entry per cited id; the SAME underlying source (same
 * document, or same tool+period) can appear under multiple ids when the model
 * cites it for several facts. We dedupe by IDENTITY for display, but every emitted
 * id must still resolve so its inline marker isn't orphaned. So instead of dropping
 * duplicate ids, we map every id to a single CANONICAL id (the lowest id sharing
 * that identity). Markers render the canonical number and link to the one panel
 * entry — identical sources share a number, Perplexity-style.
 *
 * Genuinely-unused ids (dropped by the backend provenance guardrail) are simply
 * absent from `sources`, so their markers correctly render inert.
 */

import { CitationSource } from './MessagesContainer.types';
import { CanonicalMap, DisplayMap } from './MessagesContainer.types';
import { CITATION_SOURCE_TYPE } from './MessagesContainer.constants';

/** Stable identity key — two sources with the same key are the "same" source. */
export const sourceIdentityKey = (s: CitationSource): string => {
    if (s.type === CITATION_SOURCE_TYPE.DOCUMENT) return `d:${s.file_name}|${s.doc_id}`;
    if (s.type === CITATION_SOURCE_TYPE.TOOL) return `t:${s.display_name}|${s.group}|${s.as_of ?? ''}`;
    return 'm'; // model_knowledge — single bucket
};

/** Find the first <tr> in a hast node tree (depth-first). */
export const findFirstRow = (n: any): any => {
    if (!n?.children) return null;
    for (const child of n.children) {
        if (child.tagName === 'tr') return child;
        const found = findFirstRow(child);
        if (found) return found;
    }
    return null;
};

/** Count the columns of a markdown table from its hast node (first row's cells). */
export const countTableColumns = (node: any): number => {
    const row = findFirstRow(node);
    const cols = row?.children?.filter((c: any) => c.tagName === 'th' || c.tagName === 'td').length;
    return cols && cols > 0 ? cols : 1;
};

/**
 * Build a map of identity key → canonical id (lowest id wins per identity).
 * Extracted so buildCanonicalMap stays a flat, readable loop.
 */
const buildIdentityToCanonicalId = (sources: CitationSource[]): Record<string, number> =>
    sources.reduce<Record<string, number>>((acc, s) => {
        const key = sourceIdentityKey(s);
        acc[key] = acc[key] === undefined ? s.id : Math.min(acc[key], s.id);
        return acc;
    }, {});

/** Build the id→canonical map and the deduped canonical source list. */
export const buildCanonicalMap = (sources?: CitationSource[]): CanonicalMap => {
    const idToCanonical: Record<number, number> = {};
    const canonicalSources: CitationSource[] = [];
    if (!sources?.length) return { idToCanonical, canonicalSources };

    const identityToCanonicalId = buildIdentityToCanonicalId(sources);

    for (const s of sources) {
        idToCanonical[s.id] = identityToCanonicalId[sourceIdentityKey(s)];
    }

    // canonicalSources: the entry whose id IS the canonical id for its identity.
    const seen = new Set<string>();
    for (const s of sources) {
        const key = sourceIdentityKey(s);
        if (s.id === identityToCanonicalId[key] && !seen.has(key)) {
            seen.add(key);
            canonicalSources.push(s);
        }
    }

    return { idToCanonical, canonicalSources };
};

/**
 * Scan content for ‹cite:N› and ‹table-sources:…› sentinels in document order
 * and return the raw citation ids in first-appearance order.
 */
const extractCitationOrderFromContent = (content: string): number[] => {
    const order: number[] = [];
    const refRe = /‹cite:(\d+(?:\s*,\s*\d+)*)›|‹table-sources:([\d,\s]+)›/g;
    let m: RegExpExecArray | null;
    while ((m = refRe.exec(content)) !== null) {
        for (const part of (m[1] ?? m[2]).split(',')) {
            const id = parseInt(part.trim(), 10);
            if (!Number.isNaN(id)) order.push(id);
        }
    }
    return order;
};

/**
 * Assign READING-ORDER display numbers: the first reference encountered in the
 * answer text becomes [1], the next new one [2], and so on — regardless of the
 * ids the model assigned. Scans both inline ‹cite:N› markers and
 * ‹table-sources:…› lists in document order; sources never referenced in the
 * text (edge case) are numbered after, in id order.
 *
 * model_knowledge sources get NO display number and are excluded from
 * orderedSources — model reasoning is never surfaced as a reference
 * (product decision, 2026-07-13).
 */
export const buildDisplayMap = (
    content: string | undefined,
    idToCanonical: Record<number, number>,
    canonicalSources: CitationSource[],
): DisplayMap => {
    const byCanonical: Record<number, CitationSource> = {};
    for (const s of canonicalSources) byCanonical[s.id] = s;

    const displayNumbers: Record<number, number> = {};
    const orderedSources: CitationSource[] = [];
    let next = 1;

    const rawIds = content ? extractCitationOrderFromContent(content) : [];

    for (const rawId of rawIds) {
        const canonical = idToCanonical[rawId] ?? rawId;
        const source = byCanonical[canonical];
        if (!source || source.type === CITATION_SOURCE_TYPE.MODEL_KNOWLEDGE) continue;
        if (displayNumbers[canonical] !== undefined) continue;
        displayNumbers[canonical] = next++;
        orderedSources.push(source);
    }

    // Any displayable source never referenced inline still gets a number (after
    // the inline ones), so the panel can render it consistently.
    for (const s of canonicalSources) {
        if (s.type !== CITATION_SOURCE_TYPE.MODEL_KNOWLEDGE && displayNumbers[s.id] === undefined) {
            displayNumbers[s.id] = next++;
            orderedSources.push(s);
        }
    }

    return { displayNumbers, orderedSources };
};
